import React, { FC, useState } from 'react';
import Input from 'react-phone-number-input/input';
import {
  E164Number,
  CountryCode,
  parsePhoneNumberFromString,
} from 'libphonenumber-js';
import metadata from 'libphonenumber-js/metadata.min.json';
import CloseIcon from '@/components/ui/Icons/CloseIcon';

export const getCountryCodeFromLocale = (
  locale: string | undefined,
): CountryCode => {
  if (!locale) {
    return 'US';
  }

  const localeParts = locale.split('-');
  const country =
    localeParts.length > 1
      ? localeParts[1].toUpperCase()
      : localeParts[0].toUpperCase();

  const countryCodes = metadata.countries as Record<string, any>;
  if (country in countryCodes) {
    return country as CountryCode;
  }

  return 'US';
};

export type PhoneNumberInputProps = {
  value: string;
  onChange: (value: string) => void;
  locale: string | undefined;
  placeholder?: string;
  errorMessage: string;
  onClearError?: () => void;
  onValidationError?: () => void;
};

const PhoneNumberInput: FC<PhoneNumberInputProps> = ({
  value,
  onChange,
  locale,
  placeholder,
  errorMessage,
  onClearError,
  onValidationError,
}) => {
  const [errorValue, setErrorValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  const countryCode = getCountryCodeFromLocale(locale);

  const validatePhoneNumber = (value: string) => {
    if (value == '') {
      setErrorValue('');
      onClearError && onClearError();
      return;
    }
    const parsedPhoneNumber = parsePhoneNumberFromString(value, countryCode);
    if (parsedPhoneNumber && parsedPhoneNumber.isValid()) {
      setErrorValue('');
      onClearError && onClearError();
    } else {
      setErrorValue(errorMessage);
      onValidationError && onValidationError();
    }
  };

  const handleInputChange = (value: E164Number | undefined) => {
    const trimmedValue = value ? value.trim() : '';
    onChange(trimmedValue);
    validatePhoneNumber(value || '');
  };

  const handleClear = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    handleInputChange(undefined);
  };

  return (
    <div>
      <div style={{ position: 'relative' }}>
        <Input
          value={value}
          onChange={handleInputChange}
          placeholder={placeholder}
          country={countryCode}
          className={`w-full border px-4 py-3 rounded-sm ${
            errorValue ? 'border-warning' : 'border-grey-midtone'
          }`}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
        />
        {value && isFocused && (
          <button
            onMouseDown={handleClear}
            className="right-4 top-1/2 transform -translate-y-1/2 absolute text-blue-deep"
          >
            <CloseIcon />
          </button>
        )}
      </div>
      {errorValue && <p className="text-warning mt-1 text-sm">{errorValue}</p>}
    </div>
  );
};

export default PhoneNumberInput;
